import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { BaseComponent } from '../abstract/base.component';
import { AppConstants } from '../shared/constants/app-constants';
import { DataManagerService } from '../data/services/DataManager/data-manager.service';
import { ExelService } from '../data/services/Exel/exel.service';
import { AlertMessageDialogComponent } from '../dialogs/alert-message-dialog/alert-message-dialog.component';

const ERROR_MESSAGE = 'Unable to load VAMP data. Please try again later.';
const NO_DATA_MESSAGE = 'No VAMP data available.';

/**
 * VAMP Details — a single self-contained page: the merchant's VAMP summary in one details card,
 * and the TC-40 transactions that make up the ratio in a table below (server-side search, sort
 * and pagination; client-side CSV/XLS export). One shared From/To filter drives both.
 */
@Component({
    selector: 'app-vamp',
    templateUrl: './vamp.component.html',
    styleUrls: ['./vamp.component.css'],
    providers: [ExelService],
    standalone: false
})
export class VampComponent extends BaseComponent implements OnInit {

  readonly noDataMessage = NO_DATA_MESSAGE;
  today = new Date();
  from: Date;
  to: Date;

  // summary (one details card; zeros/defaults when no data)
  summary: any = null;
  mid: string = null;

  readonly summaryFields = [
    { key: 'mid', label: 'MID', type: 'text' },
    { key: 'legal_name', label: 'Legal Name', type: 'text' },
    { key: 'dba_name', label: 'DBA Name', type: 'text' },
    { key: 'agent_name', label: 'Agent/Referral Partner', type: 'text' },
    { key: 'status', label: 'Status', type: 'text' },
    // Reserve Amount, Sale Count, Reported Fraud Count, Chargeback Count and Chargebacks - RDR
    // are intentionally hidden from the merchant dashboard (frontend-only). The API still returns
    // them and VAMP Count/Ratio are still computed from them server-side — just not displayed here.
    { key: 'vamp_count', label: 'VAMP Count', type: 'number' },
    { key: 'vamp_ratio_pct', label: 'VAMP Ratio', type: 'percent' },
  ];

  // transactions table
  rows: any[] = [];
  searchText: string;
  searchHits = new Subject<void>();
  orderBy: string;
  sortBy: 'asc' | 'desc' = 'asc';
  stylesortBy = 'fa-angle-up';

  // Widths sized to fit each header (text + sort caret + cell padding) at the table font.
  // The table grows to the sum of these and scrolls horizontally (see component CSS), so these
  // are absolute — headers never get scaled down/cropped, and short columns stay compact.
  readonly columns = [
    { key: 'purchase_date', label: 'Purchase Date', type: 'date', width: 145 },
    { key: 'fraud_main_date', label: 'Fraud Main Date', type: 'date', width: 165 },
    { key: 'fraud_post_date', label: 'Fraud Post Date', type: 'date', width: 165 },
    { key: 'card_account_number', label: 'Card Account Number', type: 'text', width: 210 },
    { key: 'transaction_id', label: 'Transaction ID', type: 'text', width: 160 },
    { key: 'card_acceptor_id', label: 'Card Acceptor ID (MID)', type: 'text', width: 235 },
    { key: 'acquirer_reference_number', label: 'Acquirer Reference Number', type: 'text', width: 270 },
    { key: 'merchant_name', label: 'Merchant Name', type: 'text', width: 180 },
    { key: 'fraud_type_1', label: 'Fraud Type 1', type: 'text', width: 150 },
    { key: 'fraud_type_2', label: 'Fraud Type 2', type: 'text', width: 150 },
    { key: 'fraud_amount', label: 'Fraud Amount', type: 'money', width: 145 },
  ];

  page = 1;
  totalRecords = 0;
  totalPages = 1;
  readonly limitArray = [25, 50, 100];

  private summaryUrl: string;
  private transactionsUrl: string;

  constructor(
    router: Router,
    dataManager: DataManagerService,
    snackBar: MatSnackBar,
    private datePipe: DatePipe,
    private exelService: ExelService,
    private dialog: MatDialog,
  ) {
    super();
    this.router = router;
    this.dataManager = dataManager;
    this.snackBar = snackBar;
    this.limit = 25;

    const now = new Date();
    this.from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    this.to = new Date(now.getFullYear(), now.getMonth(), 0);

    this.summaryUrl = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_VAMP + '/summary';
    this.transactionsUrl = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_VAMP + '/transactions';
  }

  ngOnInit() {
    // Stored on filterSubscription so BaseComponent.ngOnDestroy unsubscribes it (no leak).
    this.filterSubscription = this.searchHits.pipe(debounceTime(600))
      .subscribe(() => { this.page = 1; this.loadTransactions(); });
    this.loadSummary();
  }

  // ---- summary ----
  loadSummary() {
    this.errorMsg = null;
    this.loadFlag = true;
    const url = this.summaryUrl + '?from=' + this.fmt(this.from) + '&to=' + this.fmt(this.to);
    this.dataManager.getRequest(url, 0).subscribe(
      res => {
        this.loadFlag = false;
        const result = res && res.data ? res.data : [];
        this.summary = result.length ? result[0] : null;
        this.mid = this.summary ? this.summary.mid : null;
        this.rows = []; this.totalRecords = 0; this.totalPages = 1; this.page = 1;
        if (this.mid) { this.loadTransactions(); }
      },
      err => {
        this.loadFlag = false;
        this.summary = null; this.mid = null; this.rows = []; this.totalRecords = 0;
        this.showApiError(err);
      }
    );
  }

  /** Summary always renders: defaults/zeros when there's no data for the period. */
  get displaySummary(): any {
    return this.summary || {
      mid: '--', legal_name: '--', dba_name: '--', agent_name: '--', status: '--',
      reserve_amount: 0, sale_count: 0, reported_fraud_count: 0, chargeback_count: 0,
      chargebacks_minus_rdr: 0, vamp_count: 0, vamp_ratio_pct: 0,
    };
  }

  // ---- transactions ----
  loadTransactions() {
    if (!this.mid) { return; }
    this.loadFlag2 = true;
    this.dataManager.getRequest(this.buildUrl(this.limit, this.page), 0).subscribe(
      res => {
        this.loadFlag2 = false;
        this.rows = res && res.data ? res.data : [];
        const pg = res && res.meta ? res.meta.pagination : null;
        this.totalRecords = pg ? pg.total : this.rows.length;
        this.totalPages = pg ? pg.total_pages : 1;
        this.page = pg ? pg.current_page : 1;
      },
      err => {
        this.loadFlag2 = false;
        this.rows = []; this.totalRecords = 0; this.totalPages = 1;
        this.showApiError(err);
      }
    );
  }

  private buildUrl(limit: number, page: number): string {
    let url = this.transactionsUrl + '/' + encodeURIComponent(this.mid)
      + '?from=' + this.fmt(this.from) + '&to=' + this.fmt(this.to) + '&limit=' + limit + '&page=' + page;
    if (this.orderBy) { url += '&orderBy=' + this.orderBy + '&sortedBy=' + this.sortBy; }
    if (!this.isNullOrUndefined(this.searchText) && this.searchText !== '') {
      url += '&search=' + encodeURIComponent(this.searchText);
    }
    return url;
  }

  onDateChange() { this.page = 1; this.loadSummary(); }

  setSorting(orderBy: string) {
    if (orderBy === this.orderBy) {
      this.sortBy = this.sortBy === 'asc' ? 'desc' : 'asc';
    } else {
      this.orderBy = orderBy; this.sortBy = 'asc';
    }
    this.stylesortBy = this.sortBy === 'asc' ? 'fa-angle-up' : 'fa-angle-down';
    this.page = 1;
    this.loadTransactions();
  }

  get rangeStart(): number { return this.totalRecords ? (this.page - 1) * this.limit + 1 : 0; }
  get rangeEnd(): number { return Math.min(this.page * this.limit, this.totalRecords); }
  setUrl() { this.page = 1; this.loadTransactions(); }
  prevPage() { if (this.page > 1) { this.page--; this.loadTransactions(); } }
  nextPage() { if (this.page < this.totalPages) { this.page++; this.loadTransactions(); } }

  // ---- export (transactions only, client-side) ----
  export(format: 'csv' | 'xls') {
    if (!this.totalRecords) { this.showSnackBar('Nothing to export.', 'OK', 3000); return; }
    this.loadFlag2 = true;
    this.dataManager.getRequest(this.buildUrl(0, 1), 0).subscribe(
      res => {
        this.loadFlag2 = false;
        const data = res && res.data ? res.data : [];
        const mapped = data.map(r => {
          const o: any = {};
          this.columns.forEach(c => { o[c.label] = r[c.key]; });
          return o;
        });
        const fileName = 'VAMP_Transactions_' + this.mid;
        if (format === 'csv') { this.exelService.exportAsCsvFile(mapped, fileName); }
        else { this.exelService.exportAsExcelFile(mapped, fileName); }
      },
      () => { this.loadFlag2 = false; this.showSnackBar('Unable to export VAMP data.', 'OK', 4000); }
    );
  }

  get disputeMailto(): string {
    const dba = this.summary ? (this.summary.dba_name || '') : '';
    const subject = encodeURIComponent('VAMP Inquiry/Dispute (' + (this.mid || '') + ' & ' + dba + ')');
    return 'mailto:Risk@payarc.com?subject=' + subject;
  }

  /**
   * Show the error in a (large) message-box dialog — only for genuine failures. A 404 means
   * "no data", which the page already conveys via the empty state, so it raises no dialog.
   * The message is whatever the API returns in `error`: the backend sends the real exception
   * text in debug mode and the friendly fallback otherwise — so 401/session is still handled.
   */
  private showApiError(err: any): void {
    if (!err || err.status === 404) { return; }
    const body = err.error !== undefined ? err.error : err;
    this.networkErrorHandler(body);
    const message = body && typeof body.message === 'string' && body.message ? body.message : ERROR_MESSAGE;
    this.dialog.open(AlertMessageDialogComponent, {
      data: { head: 'Error', message, isIcoEnable: true },
      width: '560px',
      maxWidth: '92vw',
      panelClass: 'vamp-error-dialog',
    });
  }

  private fmt(d: Date): string { return this.datePipe.transform(d, 'yyyy-MM-dd'); }
}
